import React, { useEffect, useMemo, useState } from "react";
import { Badge, Descriptions, Modal, Space, Tag, Typography, message } from "antd";
import SpreadsheetGrid from "../../../../components/common/SpreadsheetGrid";
import { measurementUnitApi } from "../../../../api/v3/masterDataApi";
import { buildProductGridColumns } from "./productGridConfig";

const { Text } = Typography;

const SCENARIO_CONDITION_DISPLAY_KEYS = new Set(["supplier_name", "supplier_code", "supplier_display_name"]);
const GRID_ID_FIELD = "_grid_id";

const getSupplierConditionDisplay = (conditions) => {
    if (!conditions) return "";
    if (conditions.supplier_display_name) return conditions.supplier_display_name;
    if (conditions.supplier_code && conditions.supplier_name) return `${conditions.supplier_code} - ${conditions.supplier_name}`;
    return conditions.supplier_name || conditions.supplier_id;
};

/**
 * 产品列表查看器组件（只读模式弹窗）。
 */
const ProductListViewer = ({ visible, onClose, record }) => {
    const products = record?.json_content?.products || [];
    const [unitOptions, setUnitOptions] = useState([]);
    const [unitsLoading, setUnitsLoading] = useState(false);

    useEffect(() => {
        if (!visible) return undefined;
        let ignore = false;
        const loadUnits = async () => {
            setUnitsLoading(true);
            try {
                const response = await measurementUnitApi.listUnits({ page: 1, page_size: 500, is_active: true });
                if (ignore) return;
                setUnitOptions((response.data?.items || []).map((unit) => ({
                    value: unit.code,
                    label: unit.display_name && unit.display_name !== unit.code ? `${unit.display_name} (${unit.code})` : unit.code,
                    displayName: unit.display_name,
                })));
            } catch (error) {
                if (!ignore) message.error("加载单位列表失败");
            } finally {
                if (!ignore) setUnitsLoading(false);
            }
        };
        loadUnits();
        return () => {
            ignore = true;
        };
    }, [visible]);

    const rows = useMemo(
        () => products.map((product, index) => ({ ...product, [GRID_ID_FIELD]: `${product.product_code || "product"}-${index}` })),
        [products],
    );

    const columns = useMemo(
        () => buildProductGridColumns({ unitOptions, unitsLoading }).map((column) => ({ ...column, editable: false })),
        [unitOptions, unitsLoading],
    );

    const renderScenarioConditions = () => {
        const conditions = record?.scenario_conditions;
        if (!conditions || Object.keys(conditions).length === 0) {
            return <Text type="secondary">无</Text>;
        }

        return (
            <Space wrap>
                {Object.entries(conditions)
                    .filter(([key]) => !SCENARIO_CONDITION_DISPLAY_KEYS.has(key))
                    .map(([key, value]) => (
                        <Tag key={key} color="blue">
                            {key}: {key === "supplier_id" ? getSupplierConditionDisplay(conditions) : value}
                        </Tag>
                    ))}
            </Space>
        );
    };

    return (
        <Modal
            title={(
                <Space>
                    <span>产品知识库详情</span>
                    <Badge count={products.length} style={{ backgroundColor: "#52c41a" }} />
                </Space>
            )}
            className="large-modal"
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1160}
            styles={{ body: { maxHeight: "none", overflowY: "visible" } }}
        >
            <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Agent 名称">{record?.agent_name}</Descriptions.Item>
                <Descriptions.Item label="状态">
                    <Badge status={record?.is_active ? "success" : "error"} text={record?.is_active ? "启用" : "停用"} />
                </Descriptions.Item>
                <Descriptions.Item label="场景条件" span={2}>
                    {renderScenarioConditions()}
                </Descriptions.Item>
                {record?.description && (
                    <Descriptions.Item label="描述" span={2}>
                        {record.description}
                    </Descriptions.Item>
                )}
            </Descriptions>

            <SpreadsheetGrid
                rows={rows}
                columns={columns}
                rowKeyGetter={(row) => row[GRID_ID_FIELD]}
                readOnly
                emptyDescription="暂无产品数据"
                rowHeight={42}
            />
        </Modal>
    );
};

export default ProductListViewer;
